import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { toast } from 'src/@/components/ui/use-toast';
import mktPostsService from 'src/services/mktPostsService';
import mktAccountsService from 'src/services/mktAccountsService';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import MktPostPreview from './MktPostPreview';
import MktPostMediaUpload from './MktPostMediaUpload';
import MktPostDeleteDialog from './MktPostDeleteDialog';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import {
    PLATFORM_OPTIONS,
    POST_TYPE_OPTIONS,
    PLATFORM_CAPS,
    STATUS_MAP,
    getMaxChars,
    getPlatformLabel,
    parseHashtags,
    hashtagsToText,
    validatePost,
    resolveValidationMessage
} from './constants';

const LIST_URL = '/dashboard/marketing/publicacoes';

function pad(n) {
    return String(n).padStart(2, '0');
}

function toLocalDateTimeParts(isoString) {
    if (!isoString) return { date: '', time: '' };
    const dt = new Date(isoString);
    return {
        date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
        time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
    };
}

function formatDateTime(value, locale) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(locale, {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

// Uma vez publicado, o que pode mudar depende da plataforma: Instagram nao
// permite editar nem remidiar nada; Facebook so permite trocar o texto.
function computeEditability(post) {
    if (!post) return { locked: false, textOnly: false, reasonKey: null };
    if (post.source === 'graph') {
        return { locked: true, textOnly: false, reasonKey: 'posts.editability.importedGraph' };
    }
    if (['draft', 'scheduled'].includes(post.status)) {
        return { locked: false, textOnly: false, reasonKey: null };
    }
    const platforms = post.platforms || [];
    if (platforms.includes('facebook')) {
        return { locked: false, textOnly: true, reasonKey: 'posts.editability.facebookTextOnly' };
    }
    return { locked: true, textOnly: false, reasonKey: 'posts.editability.instagramLocked' };
}

export default function MktPostPage({ postId }) {
    const { t } = useTranslation('marketing');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isCreate = postId === 'novo';

    const [post, setPost] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [capabilities, setCapabilities] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // Form state
    const [platforms, setPlatforms] = useState([]);
    const [postType, setPostType] = useState('feed');
    const [text, setText] = useState('');
    const [hashtagsText, setHashtagsText] = useState('');
    const [link, setLink] = useState('');
    const [media, setMedia] = useState([]);
    const [accountSelections, setAccountSelections] = useState({});
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');

    const [saving, setSaving] = useState(null); // null | 'draft' | 'schedule' | 'publish' | 'text'
    const [acting, setActing] = useState(null); // null | 'publish' | 'retry' | 'cancel'
    const [error, setError] = useState('');
    const [editingText, setEditingText] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const hydrateForm = useCallback((source, accountList) => {
        setPlatforms(source.platforms || []);
        setPostType(source.post_type || 'feed');
        setText(source.content?.text || '');
        setHashtagsText(hashtagsToText(source.content?.hashtags));
        setLink(source.content?.link || '');
        setMedia(source.content?.media || []);

        const selections = {};
        (source.account_ids || []).forEach((ref) => {
            const id = typeof ref === 'string' ? ref : ref?._id;
            const acc = (accountList || []).find((a) => a._id === id);
            if (acc) selections[acc.provider] = acc._id;
        });
        setAccountSelections(selections);

        const { date, time } = toLocalDateTimeParts(source.scheduled_date);
        setScheduleDate(date);
        setScheduleTime(time);
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [postRes, accountsRes, capsRes] = await Promise.all([
                isCreate ? Promise.resolve(null) : mktPostsService.buscar(postId),
                mktAccountsService.listar(user.clienteId),
                mktPostsService.capacidades(user.clienteId).catch(() => null)
            ]);

            const accountList = accountsRes?.data || [];
            setAccounts(accountList);
            setCapabilities(capsRes?.data || null);

            if (isCreate) {
                setPost(null);
                // Data vinda do clique num dia do calendario (?data=ISO)
                const preset = searchParams.get('data');
                if (preset) {
                    const dt = new Date(preset);
                    if (!Number.isNaN(dt.getTime())) {
                        setScheduleDate(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`);
                        setScheduleTime(`${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
                    }
                }
                // Plataforma vinda do dropdown "Nova Publicação" (?platform=instagram|facebook)
                const presetPlatform = searchParams.get('platform');
                if (presetPlatform && PLATFORM_CAPS[presetPlatform]) {
                    setPlatforms([presetPlatform]);
                    // accountsByProvider (useMemo) ainda nao reflete este fetch — usa
                    // accountList direto, mesmo padrao que hydrateForm ja usa acima.
                    const firstAccount = accountList.find((a) => a.provider === presetPlatform);
                    if (firstAccount) {
                        setAccountSelections((prev) => ({ ...prev, [presetPlatform]: firstAccount._id }));
                    }
                }
            } else {
                const loaded = postRes?.data;
                setPost(loaded);
                if (loaded) hydrateForm(loaded, accountList);
            }
        } catch (err) {
            console.error('Erro ao carregar publicação:', err);
            setLoadError(err?.message || t('posts.page.loadFailed'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postId, isCreate, user.clienteId, hydrateForm]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const accountsByProvider = useMemo(() => {
        const map = { instagram: [], facebook: [] };
        (accounts || []).forEach((a) => {
            if (map[a.provider]) map[a.provider].push(a);
        });
        return map;
    }, [accounts]);

    const capabilityByAccountId = useMemo(() => {
        const map = {};
        (capabilities?.accounts || []).forEach((a) => { map[a._id] = a; });
        return map;
    }, [capabilities]);

    const accountById = useMemo(() => {
        const map = {};
        (accounts || []).forEach((a) => { map[a._id] = a; });
        return map;
    }, [accounts]);

    const editability = computeEditability(post);
    const isFormMode = isCreate || (!editability.locked && !editability.textOnly);

    const maxChars = getMaxChars(platforms.length ? platforms : ['instagram']);
    const hashtags = parseHashtags(hashtagsText);
    const { errors: validationErrors } = validatePost({
        platforms, postType, text, media: media.filter((m) => !m._uploading), hashtags
    }, t);

    const missingScopePlatforms = platforms.filter((p) => {
        const accId = accountSelections[p];
        if (!accId) return false;
        const cap = capabilityByAccountId[accId];
        return cap && cap.canPublish === false;
    });

    const previewAccounts = useMemo(() => ({
        instagram: accountsByProvider.instagram.find((a) => a._id === accountSelections.instagram),
        facebook: accountsByProvider.facebook.find((a) => a._id === accountSelections.facebook)
    }), [accountsByProvider, accountSelections]);

    const goBack = () => navigate(LIST_URL);

    const togglePlatform = (platformId) => {
        setPlatforms((prev) => (prev.includes(platformId) ? prev.filter((p) => p !== platformId) : [...prev, platformId]));
        setAccountSelections((prev) => {
            if (prev[platformId]) return prev;
            const first = accountsByProvider[platformId]?.[0];
            return first ? { ...prev, [platformId]: first._id } : prev;
        });
    };

    const buildPayload = (status) => ({
        account_ids: platforms.map((p) => accountSelections[p]).filter(Boolean),
        platforms,
        post_type: postType,
        content: {
            text: text.trim(),
            media: media.filter((m) => !m._uploading).map(({ url, type, thumbnail }) => ({ url, type, thumbnail })),
            hashtags,
            link: link.trim() || undefined
        },
        scheduled_date: status === 'scheduled' && scheduleDate && scheduleTime
            ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
            : null,
        status
    });

    const persist = async (status) => {
        const payload = buildPayload(status);
        if (post?._id) return mktPostsService.editar(user.clienteId, post._id, payload);
        return mktPostsService.criar(user.clienteId, payload);
    };

    const runGuarded = async (kind, fn) => {
        if (validationErrors.length > 0) {
            setError(resolveValidationMessage(validationErrors[0], t));
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setSaving(kind);
        setError('');
        try {
            await fn();
        } catch (err) {
            console.error(`Erro ao salvar publicação (${kind}):`, err);
            setError(err?.message || t('posts.page.genericError'));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSaving(null);
        }
    };

    const handleSaveDraft = () => runGuarded('draft', async () => {
        await persist('draft');
        toast({ title: t('posts.toast.draftSaved') });
        goBack();
    });

    const handleSchedule = () => runGuarded('schedule', async () => {
        if (!scheduleDate || !scheduleTime) {
            setError(t('posts.form.scheduleRequired'));
            return;
        }
        await persist('scheduled');
        toast({ title: t('posts.toast.scheduled') });
        goBack();
    });

    const handlePublishNow = () => runGuarded('publish', async () => {
        const result = await persist('draft');
        const id = result?.data?._id || post?._id;
        const pub = await mktPostsService.publicar(id);

        if (pub.status === 202) {
            toast({ title: t('posts.toast.processing'), description: t('posts.toast.videoProcessingList') });
        } else if (pub.data?.status === 'partially_published') {
            toast({ title: t('posts.toast.partialPublish'), description: t('posts.toast.partialPublishPage'), variant: 'destructive' });
        } else {
            toast({ title: t('posts.toast.published') });
        }
        goBack();
    });

    // Edicao inline do texto de um post ja publicado no Facebook.
    const handleSaveTextOnly = async () => {
        setSaving('text');
        setError('');
        try {
            await mktPostsService.editar(user.clienteId, post._id, {
                content: { ...post.content, text: text.trim() }
            });
            toast({ title: t('posts.toast.textUpdated') });
            setEditingText(false);
            fetchAll();
        } catch (err) {
            console.error('Erro ao atualizar texto:', err);
            setError(err?.message || t('posts.toast.textUpdateFailed'));
        } finally {
            setSaving(null);
        }
    };

    // Acoes de estado que atualizam a propria pagina (nao saem para a lista).
    const runAction = async (kind, fn, successTitle) => {
        setActing(kind);
        try {
            const res = await fn();
            if (kind === 'publish' && res?.status === 202) {
                toast({ title: t('posts.toast.processing'), description: t('posts.toast.videoProcessing') });
            } else if (res?.data?.status === 'partially_published') {
                toast({ title: t('posts.toast.partialPublish'), description: t('posts.toast.partialPublishBelow'), variant: 'destructive' });
            } else {
                toast({ title: successTitle });
            }
            fetchAll();
        } catch (err) {
            if (err?.kind === 'MISSING_SCOPE') {
                toast({ title: t('posts.toast.insufficientScope'), description: t('posts.toast.reconnectPublish'), variant: 'destructive' });
            } else {
                toast({ title: t('common.error'), description: err?.message, variant: 'destructive' });
            }
            fetchAll();
        } finally {
            setActing(null);
        }
    };

    const handleDeleteConfirm = async ({ remote }) => {
        setDeleting(true);
        try {
            await mktPostsService.deletar(user.clienteId, post._id, { remote });
            toast({ title: t('posts.toast.deleted') });
            goBack();
        } catch (err) {
            toast({ title: t('posts.toast.deleteFailed'), description: err?.message, variant: 'destructive' });
            setDeleting(false);
        }
    };

    // ---------- Loading / erro ----------

    if (loading) {
        return (
            <div className={d.pageDetail.shell} style={{ fontFamily: d.page.fontStack }}>
                <div className={d.pageDetail.headerBar}>
                    <div className={d.page.container}>
                        <div className={d.pageDetail.headerInner}>
                            <div className={d.pageDetail.headerLeft}>
                                <button type="button" className={d.pageDetail.backBtn} onClick={goBack}>
                                    <Iconify icon="eva:arrow-back-outline" width={18} />
                                </button>
                                <div className={d.pageDetail.iconBox}>
                                    <Iconify icon="eva:edit-2-outline" width={20} />
                                </div>
                                <div className="min-w-0">
                                    <h1 className={d.pageDetail.title}>{isCreate ? t('posts.page.new') : t('posts.page.edit')}</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={d.pageDetail.body}>
                    <MktLoading text={t('common.loading')} />
                </div>
            </div>
        );
    }

    if (loadError || (!isCreate && !post)) {
        return (
            <div className={d.pageDetail.shell} style={{ fontFamily: d.page.fontStack }}>
                <div className={d.pageDetail.body}>
                    <div className={d.emptyState.container}>
                        <div className={d.emptyState.iconBox}>
                            <Iconify icon="eva:alert-circle-outline" width={28} className="text-violet-600" />
                        </div>
                        <h3 className={d.emptyState.title}>{t('posts.page.notFound')}</h3>
                        <p className={d.emptyState.subtitle}>{loadError || t('posts.page.notFoundDesc')}</p>
                        <span className={d.emptyState.action} onClick={goBack}>{t('posts.page.back')}</span>
                    </div>
                </div>
            </div>
        );
    }

    const status = post ? (STATUS_MAP[post.status] || STATUS_MAP.draft) : null;
    const title = isCreate ? t('posts.page.new') : (isFormMode ? t('posts.page.edit') : t('posts.page.view'));
    const canPublishNow = post && ['draft', 'scheduled', 'failed'].includes(post.status);
    const canRetry = post && ['failed', 'partially_published'].includes(post.status);
    const canCancel = post && post.status === 'scheduled';

    return (
        <div className={d.pageDetail.shell} style={{ fontFamily: d.page.fontStack }}>
            <div className={d.pageDetail.headerBar}>
                <div className={d.page.container}>
                    <div className={d.pageDetail.headerInner}>
                        <div className={d.pageDetail.headerLeft}>
                            <button type="button" className={d.pageDetail.backBtn} onClick={goBack} title={t('posts.page.back')}>
                                <Iconify icon="eva:arrow-back-outline" width={18} />
                            </button>
                            <div className={d.pageDetail.iconBox}>
                                <Iconify icon="eva:edit-2-outline" width={20} />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h1 className={d.pageDetail.title}>{title}</h1>
                                    {status && (
                                        <span className={d.statusBadge[status.tone]}>
                                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                            {t(status.labelKey)}
                                        </span>
                                    )}
                                    {post?.source === 'graph' && (
                                        <span className={d.pageDetail.importedTag}>
                                            <Iconify icon="eva:download-outline" width={11} />
                                            {t('posts.table.imported')}
                                        </span>
                                    )}
                                </div>
                                <p className={d.pageDetail.subtitle}>
                                    {editability.reasonKey ? t(editability.reasonKey) : t('posts.page.subtitle')}
                                </p>
                            </div>
                        </div>

                        {post && (
                            <div className={d.pageDetail.headerActions}>
                        {post.external_ids?.facebook?.permalink && (
                            <a
                                href={post.external_ids.facebook.permalink}
                                target="_blank"
                                rel="noreferrer"
                                className={d.pageDetail.secondaryBtn}
                            >
                                <Iconify icon="mdi:facebook" width={15} className="inline mr-1 text-blue-600" />
                                {t('posts.detail.viewOnFacebook')}
                            </a>
                        )}
                        {post.external_ids?.instagram?.permalink && (
                            <a
                                href={post.external_ids.instagram.permalink}
                                target="_blank"
                                rel="noreferrer"
                                className={d.pageDetail.secondaryBtn}
                            >
                                <Iconify icon="mdi:instagram" width={15} className="inline mr-1 text-pink-600" />
                                {t('posts.detail.viewOnInstagram')}
                            </a>
                        )}
                        {canCancel && (
                            <button
                                className={d.pageDetail.secondaryBtn}
                                disabled={Boolean(acting)}
                                onClick={() => runAction('cancel', () => mktPostsService.cancelar(post._id), t('posts.toast.scheduleCancelled'))}
                            >
                                <Iconify icon={acting === 'cancel' ? 'eos-icons:loading' : 'eva:slash-outline'} width={15} className="inline mr-1" />
                                {t('posts.actions.cancelSchedule')}
                            </button>
                        )}
                        {canRetry && (
                            <button
                                className={d.pageDetail.saveBtn}
                                disabled={Boolean(acting)}
                                onClick={() => runAction('retry', () => mktPostsService.retentar(post._id), t('posts.toast.retryDone'))}
                            >
                                <Iconify icon={acting === 'retry' ? 'eos-icons:loading' : 'eva:refresh-outline'} width={15} className="inline mr-1" />
                                {t('posts.actions.retry')}
                            </button>
                        )}
                        {!isFormMode && canPublishNow && (
                            <button
                                className={d.pageDetail.saveBtn}
                                disabled={Boolean(acting)}
                                onClick={() => runAction('publish', () => mktPostsService.publicar(post._id), t('posts.toast.published'))}
                            >
                                <Iconify icon={acting === 'publish' ? 'eos-icons:loading' : 'eva:paper-plane-outline'} width={15} className="inline mr-1" />
                                {t('posts.actions.publishNow')}
                            </button>
                        )}
                        <button className={d.pageDetail.dangerBtn} onClick={() => setDeleteOpen(true)}>
                            <Iconify icon="eva:trash-2-outline" width={15} className="inline mr-1" />
                            {t('posts.actions.delete')}
                        </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={d.pageDetail.body}>
            {error && (
                <div className={d.pageDetail.errorBanner}>
                    <Iconify icon="eva:alert-circle-outline" width={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <p>{error}</p>
                </div>
            )}

            {isFormMode ? (
                <FormMode
                    platforms={platforms}
                    postType={postType}
                    text={text}
                    hashtagsText={hashtagsText}
                    hashtags={hashtags}
                    link={link}
                    media={media}
                    maxChars={maxChars}
                    scheduleDate={scheduleDate}
                    scheduleTime={scheduleTime}
                    accountsByProvider={accountsByProvider}
                    accountSelections={accountSelections}
                    missingScopePlatforms={missingScopePlatforms}
                    previewAccounts={previewAccounts}
                    saving={saving}
                    onTogglePlatform={togglePlatform}
                    onAccountChange={(p, v) => setAccountSelections((prev) => ({ ...prev, [p]: v }))}
                    onPostType={setPostType}
                    onText={setText}
                    onHashtags={setHashtagsText}
                    onLink={setLink}
                    onMedia={setMedia}
                    onScheduleDate={setScheduleDate}
                    onScheduleTime={setScheduleTime}
                    onCancel={goBack}
                    onSaveDraft={handleSaveDraft}
                    onSchedule={handleSchedule}
                    onPublishNow={handlePublishNow}
                />
            ) : (
                <ViewMode
                    post={post}
                    accountById={accountById}
                    previewAccounts={previewAccounts}
                    hashtags={hashtags}
                    text={text}
                    editability={editability}
                    editingText={editingText}
                    saving={saving}
                    maxChars={maxChars}
                    onStartEditText={() => setEditingText(true)}
                    onCancelEditText={() => { setEditingText(false); setText(post.content?.text || ''); }}
                    onText={setText}
                    onSaveText={handleSaveTextOnly}
                />
            )}

            <MktPostDeleteDialog
                open={deleteOpen}
                post={post}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
            />
            </div>
        </div>
    );
}

// ============================================================
// Modo formulario (criar / editar rascunho ou agendado)
// ============================================================

function SectionTitle({ icon, children }) {
    return (
        <h3 className={d.form.sectionTitle}>
            <span className={d.pageDetail.sectionIcon}>
                <Iconify icon={icon} width={16} />
            </span>
            {children}
        </h3>
    );
}

function FormMode({
    platforms, postType, text, hashtagsText, hashtags, link, media, maxChars,
    scheduleDate, scheduleTime, accountsByProvider, accountSelections,
    missingScopePlatforms, previewAccounts, saving,
    onTogglePlatform, onAccountChange, onPostType, onText, onHashtags, onLink, onMedia,
    onScheduleDate, onScheduleTime, onCancel, onSaveDraft, onSchedule, onPublishNow
}) {
    const { t } = useTranslation('marketing');

    return (
        <>
            <div className={d.pageDetail.formGrid}>
                <div className={d.pageDetail.formColumn}>
                    {/* Plataformas */}
                    <div className={d.form.section}>
                        <SectionTitle icon="eva:globe-2-outline">{t('posts.form.platforms')}</SectionTitle>
                        <div className="flex flex-wrap items-center gap-2">
                            {PLATFORM_OPTIONS.map((platform) => {
                                const isActive = platforms.includes(platform.id);
                                const platformAccounts = accountsByProvider[platform.id] || [];
                                const disabled = platformAccounts.length === 0;
                                return (
                                    <button
                                        key={platform.id}
                                        type="button"
                                        title={disabled ? t('posts.form.noAccountConnected', { platform: t(platform.labelKey) }) : undefined}
                                        disabled={disabled}
                                        onClick={() => onTogglePlatform(platform.id)}
                                        className={isActive ? d.pageDetail.platformPillActive : d.pageDetail.platformPill}
                                    >
                                        <Iconify icon={platform.icon} width={16} className={platform.color} />
                                        <span>{t(platform.labelKey)}</span>
                                        {isActive && <Iconify icon="eva:checkmark-circle-2-fill" width={14} className="text-[#7C3AED]" />}
                                    </button>
                                );
                            })}
                        </div>

                        {platforms.map((p) => (accountsByProvider[p] || []).length > 1 && (
                            <div key={p} className="mt-4">
                                <label className={d.form.label}>{t('posts.form.account', { platform: getPlatformLabel(p, t) })}</label>
                                <select
                                    className={d.form.select}
                                    value={accountSelections[p] || ''}
                                    onChange={(e) => onAccountChange(p, e.target.value)}
                                >
                                    {accountsByProvider[p].map((acc) => (
                                        <option key={acc._id} value={acc._id}>{acc.account_name}</option>
                                    ))}
                                </select>
                            </div>
                        ))}

                        {missingScopePlatforms.length > 0 && (
                            <div className={d.pageDetail.warningBox}>
                                <Iconify icon="eva:alert-triangle-outline" width={15} className="flex-shrink-0 mt-0.5" />
                                <span>
                                    {t('posts.form.missingScopeWarning', {
                                        platforms: missingScopePlatforms.map((p) => getPlatformLabel(p, t)).join(', ')
                                    })}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className={d.form.section}>
                        <SectionTitle icon="eva:layers-outline">{t('posts.form.postType')}</SectionTitle>
                        <div className={d.pageDetail.segmented}>
                            {POST_TYPE_OPTIONS.map((type) => (
                                <button
                                    key={type.value}
                                    type="button"
                                    onClick={() => onPostType(type.value)}
                                    className={postType === type.value ? d.pageDetail.segmentBtnActive : d.pageDetail.segmentBtn}
                                >
                                    {t(type.labelKey)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={d.form.section}>
                        <SectionTitle icon="eva:file-text-outline">{t('posts.form.content')}</SectionTitle>
                        <label className={d.form.label}>{t('posts.form.textLabel')}</label>
                        <textarea
                            className={d.form.textarea}
                            placeholder={t('posts.form.textPlaceholder')}
                            value={text}
                            onChange={(e) => onText(e.target.value)}
                            maxLength={maxChars}
                        />
                        <div className="flex justify-end mt-1">
                            <span className={`${d.pageDetail.charCount} ${text.length > maxChars * 0.9 ? d.pageDetail.charCountWarn : d.pageDetail.charCountOk}`}>
                                {text.length}/{maxChars}
                            </span>
                        </div>

                        <label className={`${d.form.label} mt-4`}>{t('posts.form.hashtags')}</label>
                        <input
                            type="text"
                            className={d.form.input}
                            placeholder="#marketing #socialmedia #lais"
                            value={hashtagsText}
                            onChange={(e) => onHashtags(e.target.value)}
                        />

                        {postType === 'feed' && platforms.includes('facebook') && (
                            <>
                                <label className={`${d.form.label} mt-4`}>{t('posts.form.linkOptional')}</label>
                                <input
                                    type="text"
                                    className={d.form.input}
                                    placeholder="https://..."
                                    value={link}
                                    onChange={(e) => onLink(e.target.value)}
                                />
                            </>
                        )}
                    </div>

                    <div className={d.form.section}>
                        <SectionTitle icon="eva:image-outline">{t('posts.form.media')}</SectionTitle>
                        <MktPostMediaUpload
                            value={media}
                            onChange={onMedia}
                            max={postType === 'story' ? 1 : 10}
                        />
                    </div>

                    <div className={d.form.section}>
                        <SectionTitle icon="eva:clock-outline">{t('posts.form.schedule')}</SectionTitle>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={d.form.label}>{t('posts.form.date')}</label>
                                <input
                                    type="date"
                                    className={d.form.input}
                                    value={scheduleDate}
                                    onChange={(e) => onScheduleDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={d.form.label}>{t('posts.form.time')}</label>
                                <input
                                    type="time"
                                    className={d.form.input}
                                    value={scheduleTime}
                                    onChange={(e) => onScheduleTime(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={d.pageDetail.previewColumn}>
                    <p className={d.pageDetail.previewLabel}>{t('posts.form.preview')}</p>
                    <MktPostPreview
                        platforms={platforms}
                        postType={postType}
                        text={text}
                        hashtags={hashtags}
                        media={media}
                        link={link}
                        accounts={previewAccounts}
                        scheduledDate={scheduleDate}
                    />
                </div>
            </div>

            <div className={d.pageDetail.footer}>
                <button type="button" className={d.pageDetail.cancelBtn} onClick={onCancel} disabled={Boolean(saving)}>
                    {t('common.cancel')}
                </button>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <button type="button" className={d.pageDetail.secondaryBtn} onClick={onSaveDraft} disabled={Boolean(saving)}>
                        {saving === 'draft' ? t('common.saving') : t('posts.actions.saveDraft')}
                    </button>
                    <button
                        type="button"
                        className={d.pageDetail.secondaryBtn}
                        onClick={onSchedule}
                        disabled={Boolean(saving) || !scheduleDate || !scheduleTime}
                        title={!scheduleDate || !scheduleTime ? t('posts.form.scheduleRequired') : undefined}
                    >
                        {saving === 'schedule' ? t('posts.page.scheduling') : t('posts.actions.schedule')}
                    </button>
                    <button
                        type="button"
                        className={d.pageDetail.saveBtn}
                        onClick={onPublishNow}
                        disabled={Boolean(saving) || platforms.length === 0 || missingScopePlatforms.length > 0}
                        title={missingScopePlatforms.length > 0 ? t('posts.form.reconnectToPublish') : undefined}
                    >
                        {saving === 'publish' ? (
                            <>
                                <Iconify icon="eos-icons:loading" width={16} />
                                {t('posts.page.publishing')}
                            </>
                        ) : (
                            <>
                                <Iconify icon="eva:paper-plane-outline" width={16} />
                                {t('posts.actions.publishNow')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
}

// ============================================================
// Modo leitura (publicado / falhou / importado)
// ============================================================

function ViewMode({
    post, accountById, previewAccounts, hashtags, text, editability,
    editingText, saving, maxChars, onStartEditText, onCancelEditText, onText, onSaveText
}) {
    const { t, i18n } = useTranslation('marketing');
    const media = post.content?.media || [];
    const perf = post.performance || {};
    const hasPerf = ['likes', 'comments', 'shares', 'reach', 'impressions'].some((k) => perf[k]);

    const perfItems = [
        { key: 'likes', label: t('posts.detail.likes'), icon: 'eva:heart-outline' },
        { key: 'comments', label: t('posts.detail.comments'), icon: 'eva:message-circle-outline' },
        { key: 'shares', label: t('posts.detail.shares'), icon: 'eva:share-outline' },
        { key: 'reach', label: t('posts.detail.reach'), icon: 'eva:eye-outline' },
        { key: 'impressions', label: t('posts.detail.impressions'), icon: 'eva:bar-chart-outline' }
    ].filter((i) => perf[i.key] !== undefined && perf[i.key] !== null);

    return (
        <div className={d.pageDetail.readGrid}>
            <div className="space-y-5 min-w-0">
                {/* Conteudo */}
                <div className={d.pageDetail.card}>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className={`${d.pageDetail.cardTitle} mb-0`}>
                            <Iconify icon="eva:file-text-outline" width={18} className="text-violet-600" />
                            {t('posts.form.content')}
                        </h3>
                        {editability.textOnly && !editingText && (
                            <button
                                className="text-xs font-medium text-[#7C3AED] hover:text-[#6d28d9] flex items-center gap-1"
                                onClick={onStartEditText}
                            >
                                <Iconify icon="eva:edit-2-outline" width={14} />
                                {t('posts.actions.editText')}
                            </button>
                        )}
                    </div>

                    {editingText ? (
                        <>
                            <textarea
                                className={d.form.textarea}
                                value={text}
                                onChange={(e) => onText(e.target.value)}
                                maxLength={maxChars}
                            />
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-gray-400">{text.length}/{maxChars}</span>
                                <div className="flex items-center gap-2">
                                    <button className={d.pageDetail.cancelBtn} onClick={onCancelEditText} disabled={Boolean(saving)}>
                                        {t('common.cancel')}
                                    </button>
                                    <button className={d.pageDetail.saveBtn} onClick={onSaveText} disabled={Boolean(saving)}>
                                        {saving === 'text' ? t('common.saving') : t('posts.actions.saveText')}
                                    </button>
                                </div>
                            </div>
                            <p className="text-[11px] text-amber-600 mt-2 flex items-start gap-1">
                                <Iconify icon="eva:alert-triangle-outline" width={12} className="flex-shrink-0 mt-0.5" />
                                {t('posts.page.facebookEditWarning')}
                            </p>
                        </>
                    ) : (
                        <>
                            {post.content?.text ? (
                                <p className={d.pageDetail.readText}>{post.content.text}</p>
                            ) : (
                                <p className={d.pageDetail.emptyText}>{t('posts.detail.noText')}</p>
                            )}

                            {hashtags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {hashtags.map((h) => (
                                        <span key={h} className={d.pageDetail.hashtagChip}>{h}</span>
                                    ))}
                                </div>
                            )}

                            {post.content?.link && (
                                <a
                                    href={post.content.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`${d.pageDetail.linkChip} mt-3`}
                                >
                                    <Iconify icon="eva:link-2-outline" width={13} />
                                    {post.content.link}
                                </a>
                            )}
                        </>
                    )}
                </div>

                {/* Midia */}
                {media.length > 0 && (
                    <div className={d.pageDetail.card}>
                        <h3 className={d.pageDetail.cardTitle}>
                            <Iconify icon="eva:image-outline" width={18} className="text-violet-600" />
                            {t('posts.form.media')}
                            <span className="text-xs font-normal text-gray-400">({media.length})</span>
                        </h3>
                        <div className={d.pageDetail.mediaGallery}>
                            {media.map((item, idx) => (
                                <a
                                    key={item.url || idx}
                                    href={item.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={d.pageDetail.mediaItem}
                                >
                                    {item.type === 'video' ? (
                                        // eslint-disable-next-line jsx-a11y/media-has-caption
                                        <video src={`${item.url}#t=0.1`} preload="metadata" className={d.pageDetail.mediaThumb} />
                                    ) : (
                                        <img src={item.url} alt="" className={d.pageDetail.mediaThumb} />
                                    )}
                                    <span className={d.pageDetail.mediaBadge}>{item.type === 'video' ? t('posts.detail.video') : t('posts.detail.image')}</span>
                                    {item.type === 'video' && (
                                        <span className="absolute inset-0 flex items-center justify-center">
                                            <Iconify icon="eva:play-circle-fill" width={30} className="text-white/90 drop-shadow" />
                                        </span>
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Resultado por plataforma */}
                <div className={d.pageDetail.card}>
                    <h3 className={d.pageDetail.cardTitle}>
                        <Iconify icon="eva:globe-2-outline" width={18} className="text-violet-600" />
                        {t('posts.detail.resultByPlatform')}
                    </h3>
                    {(post.platforms || []).map((p) => {
                        const caps = PLATFORM_CAPS[p];
                        const result = post.external_ids?.[p] || {};
                        const account = accountById[result.account_id];
                        const st = result.status;
                        const dot = st === 'published' ? 'bg-green-500'
                            : st === 'failed' ? 'bg-red-500'
                            : st === 'publishing' ? 'bg-amber-500 animate-pulse'
                            : 'bg-gray-300';
                        const stLabel = st === 'published' ? t('posts.detail.published')
                            : st === 'failed' ? t('posts.detail.failed')
                            : st === 'publishing' ? t('posts.detail.processing')
                            : t('posts.detail.notSent');

                        return (
                            <div key={p} className={d.pageDetail.platformRow}>
                                <div className={`${d.pageDetail.platformIconBox} ${p === 'facebook' ? 'bg-blue-50' : 'bg-pink-50'}`}>
                                    <Iconify icon={caps.icon} width={18} className={caps.color} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={d.pageDetail.platformName}>{getPlatformLabel(p, t)}</span>
                                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                            <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                            {stLabel}
                                        </span>
                                    </div>
                                    {account && <p className={d.pageDetail.platformAccount}>{account.account_name}</p>}
                                    {result.error && (
                                        <p className={d.pageDetail.platformError}>
                                            <Iconify icon="eva:alert-circle-outline" width={12} className="flex-shrink-0 mt-0.5" />
                                            <span>{result.error}</span>
                                        </p>
                                    )}
                                    {result.permalink && (
                                        <a href={result.permalink} target="_blank" rel="noreferrer" className={`${d.pageDetail.platformLink} mt-1`}>
                                            {t('posts.detail.openPost')}
                                            <Iconify icon="eva:external-link-outline" width={12} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Desempenho */}
                {hasPerf && (
                    <div className={d.pageDetail.card}>
                        <h3 className={d.pageDetail.cardTitle}>
                            <Iconify icon="eva:trending-up-outline" width={18} className="text-violet-600" />
                            {t('posts.detail.performance')}
                        </h3>
                        <div className={d.pageDetail.perfGrid}>
                            {perfItems.map((item) => (
                                <div key={item.key} className={d.pageDetail.perfCard}>
                                    <Iconify icon={item.icon} width={16} className="text-gray-400 mx-auto mb-1" />
                                    <p className={d.pageDetail.perfValue}>{formatNumber(perf[item.key])}</p>
                                    <p className={d.pageDetail.perfLabel}>{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Coluna direita */}
            <div className="space-y-5">
                <div className={d.pageDetail.previewColumn}>
                    <p className={d.pageDetail.previewLabel}>{t('posts.detail.result')}</p>
                    <MktPostPreview
                        platforms={post.platforms}
                        postType={post.post_type}
                        text={post.content?.text}
                        hashtags={post.content?.hashtags}
                        media={media}
                        link={post.content?.link}
                        accounts={previewAccounts}
                        scheduledDate={post.scheduled_date}
                    />
                </div>

                <div className={d.pageDetail.card}>
                    <h3 className={d.pageDetail.cardTitle}>
                        <Iconify icon="eva:info-outline" width={18} className="text-violet-600" />
                        {t('posts.detail.details')}
                    </h3>
                    <div className={d.pageDetail.kvRow}>
                        <span className={d.pageDetail.kvKey}>{t('posts.detail.format')}</span>
                        <span className={d.pageDetail.kvValue}>{t(`posts.types.${post.post_type}`)}</span>
                    </div>
                    <div className={d.pageDetail.kvRow}>
                        <span className={d.pageDetail.kvKey}>{t('posts.detail.origin')}</span>
                        <span className={d.pageDetail.kvValue}>{post.source === 'graph' ? t('posts.detail.imported') : t('posts.detail.createdInLais')}</span>
                    </div>
                    {post.scheduled_date && (
                        <div className={d.pageDetail.kvRow}>
                            <span className={d.pageDetail.kvKey}>{t('posts.detail.scheduledFor')}</span>
                            <span className={d.pageDetail.kvValue}>{formatDateTime(post.scheduled_date, i18n.language)}</span>
                        </div>
                    )}
                    {post.published_date && (
                        <div className={d.pageDetail.kvRow}>
                            <span className={d.pageDetail.kvKey}>{t('posts.detail.publishedAt')}</span>
                            <span className={d.pageDetail.kvValue}>{formatDateTime(post.published_date, i18n.language)}</span>
                        </div>
                    )}
                    <div className={d.pageDetail.kvRow}>
                        <span className={d.pageDetail.kvKey}>{t('posts.detail.createdAt')}</span>
                        <span className={d.pageDetail.kvValue}>{formatDateTime(post.created_at, i18n.language)}</span>
                    </div>
                    {post.publish_attempts > 0 && (
                        <div className={d.pageDetail.kvRow}>
                            <span className={d.pageDetail.kvKey}>{t('posts.page.attempts')}</span>
                            <span className={d.pageDetail.kvValue}>{post.publish_attempts}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
